import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AgentFeedbackCreateInput } from '@sb/feathers-types';
import { usePageRobotRoutine } from '@sbrc/contexts';
import { useRouter } from '@sbrc/hooks';
import { getApp } from '@sbrc/services/feathers-client/utils';

import type { ChatHeaderMenuItem } from './ChatHeader';
import { ChatHistoryView } from './ChatHistoryView';
import { ChatInput } from './ChatInput';
import { ChatPanelOverlay } from './ChatPanelOverlay';
import {
  ChatPanelProvider,
  useChatPanel,
  type PendingConfirmation,
} from './ChatPanelProvider';
import { type ChatTransport, createRealChatTransport } from './ChatTransport';
import {
  ConfirmationModal,
  type ConfirmationDecision,
} from './ConfirmationModal';
import {
  LandingHero,
  type LandingOption,
  landingOptionLabel,
} from './LandingHero';
import { MessageFeedback, type FeedbackPayload } from './MessageFeedback';
import { MessageList } from './MessageList';
import type { ChatMessage } from './types';
import { useChatHistory, type ChatHistoryOperations } from './useChatHistory';
import { useChatMessages } from './useChatMessages';
import { useNavigateEventHandler } from './useNavigateEventHandler';
import { useResolveConfirmation } from './useResolveConfirmation';

interface RoutineAgentChatBodyProps {
  transport: ChatTransport;
}

/**
 * Inner body of the Routine Agent Chat mount. Pulled out so it can
 * read `useChatPanel()` (which throws when used outside the
 * provider) and `usePageRobotRoutine()` (which mirrors the routine
 * currently open in the editor).
 *
 * Backend interaction is routed through the injected `transport`;
 * the events subscription, message state, and send pipeline live
 * in `useChatMessages` so this component only handles layout +
 * slot wiring.
 *
 * Mounting lives at the router-root layout so a React Router push
 * doesn't unmount the panel — the chat survives navigation between
 * pages and the conversation state doesn't reset.
 */
export function RoutineAgentChatBody({ transport }: RoutineAgentChatBodyProps) {
  const { robot, routine } = usePageRobotRoutine();
  const { query } = useRouter();

  // Editing routine = URL-derived only (no fallback). Distinguishes
  // "the routine the user has open in the editor" from
  // `routine?.id`, which falls back to `robot.latestRoutineID` off
  // `/routines/:id`. The agent uses this to resolve "this routine"
  // unambiguously against the page the operator is on.
  const urlRoutineID = Array.isArray(query.routineID)
    ? query.routineID[0]
    : query.routineID;

  const editingRoutineID =
    typeof urlRoutineID === 'string' && urlRoutineID.length > 0
      ? urlRoutineID
      : null;

  // Loaded routine = `robot.latestRoutineID`. The routine the robot
  // will run on Play, regardless of which routine the editor is
  // showing. Lets the agent resolve "what's loaded" / "the running
  // routine" against the right id even when editing diverges.
  const loadedRoutineID = robot?.latestRoutineID ?? null;

  const {
    isOpen,
    close,
    currentSessionID,
    setCurrentSessionID,
    setCurrentRoutineID,
    pendingConfirmation,
    setPendingConfirmation,
    chatView,
    setChatView,
    startNewChat,
  } = useChatPanel();

  // Dispatch backend-emitted `navigate` events into react-router.
  // The handler is stable-by-ref so it doesn't churn the events
  // subscription. Pulling `useNavigate` from the router context
  // works here because the mount lives below `<RootLayout />`,
  // which is itself a child of `<RouterProvider>`.
  const navigate = useNavigate();
  const dispatchNavigate = useNavigateEventHandler({ navigate });

  // Monotonic counter for navigation-card React keys. Deriving the key from
  // url+label collides when the agent offers the same destination twice in a
  // turn; a per-mount sequence guarantees a unique, stable key per card.
  const navCardSeqRef = useRef(0);

  // Routes /events frames the dedicated handlers don't recognize.
  // Navigate frames flip the router; confirmation_request frames
  // open the tool-use approval modal via the provider. Approve / Reject
  // POSTs /confirm through `useResolveConfirmation` (wired below); on
  // failure the modal stays open so the user can retry.
  const handleUnknownEvent = useCallback(
    (event: unknown, append: (message: ChatMessage) => void) => {
      if (dispatchNavigate(event)) return;

      if (typeof event !== 'object' || event === null) return;

      const { type } = event as { type?: unknown };

      // `card` frames render an inline card in the transcript. Today the only
      // server-emitted kind is `navigation` (a clickable affordance that routes
      // the SPA behind the still-open chat). Any other kind — or a malformed
      // navigation payload — is dropped with a warning, for diagnostic parity
      // with the agentMessages seeding loop below.
      if (type === 'card') {
        const { cardKind, payload } = event as {
          cardKind?: unknown;
          payload?: unknown;
        };

        if (
          cardKind === 'navigation' &&
          typeof payload === 'object' &&
          payload !== null
        ) {
          const { url, label } = payload as { url?: unknown; label?: unknown };

          if (typeof url === 'string' && typeof label === 'string') {
            navCardSeqRef.current += 1;

            append({
              id: `nav-card-${navCardSeqRef.current}`,
              role: 'assistant',
              text: '',
              card: { kind: 'navigation', url, label },
            });

            return;
          }
        }

        // eslint-disable-next-line no-console -- surface dropped card frames
        console.warn(
          'RoutineAgentChatMount: dropping unsupported or malformed card frame',
          event,
        );

        return;
      }

      if (type !== 'confirmation_request') return;

      const payload = event as { toolUseID?: unknown; prompt?: unknown };

      if (
        typeof payload.toolUseID === 'string' &&
        typeof payload.prompt === 'string'
      ) {
        setPendingConfirmation({
          toolUseID: payload.toolUseID,
          prompt: payload.prompt,
        } satisfies PendingConfirmation);
      }
    },
    [dispatchNavigate, setPendingConfirmation],
  );

  const {
    messages,
    isSending,
    streamError,
    agentError,
    isThinking,
    sendMessage,
    retryMessage,
    reset,
    setMessages,
  } = useChatMessages({
    transport,
    isOpen,
    currentSessionID,
    editingRoutineID,
    loadedRoutineID,
    onUnknownEvent: handleUnknownEvent,
    // [TFIX] Pin the BE-issued sessionID into the provider on the
    // first /send of a brand-new chat. Without this, follow-up sends
    // would all POST sessionID: null and the /events subscription
    // would never open.
    onSessionCreated: setCurrentSessionID,
  });

  // POST the user's approve/reject to /confirm so the orchestrator's
  // ConfirmationGate resolves and the gated tool actually dispatches.
  // Clearing pendingConfirmation alone (the old behaviour) closed the
  // modal but left the agent's turn blocked on the gate until timeout —
  // which is why confirmation-gated tools (configure_equipment,
  // delete_routine, …) never ran after the user clicked OK.
  const { resolve: resolveConfirmation, isResolving: isResolvingConfirmation } =
    useResolveConfirmation({
      postConfirm: async ({ sessionID, toolUseID, decision }) => {
        const res = await transport.confirm({
          sessionID,
          toolUseID,
          approved: decision === 'approve',
        });

        return { status: res.status };
      },
      onResolved: () => setPendingConfirmation(null),
      // On failure, keep the modal open: the orchestrator never received
      // the decision, so dismissing here would leave the turn blocked on
      // the confirmation gate with no UI left to approve or reject.
      // `isResolving` drops in the hook's finally, re-enabling the
      // buttons so the user can retry (or Escape to reject).
      onError: () => {},
    });

  const handleConfirmationResolve = useCallback(
    (decision: ConfirmationDecision) => {
      const pending = pendingConfirmation;

      // No pending confirmation, or no session to POST the decision
      // against (a sessionless confirmation is stale by definition) —
      // nothing can be resolved server-side, so just dismiss.
      if (pending === null || currentSessionID === null) {
        setPendingConfirmation(null);

        return;
      }

      resolveConfirmation({
        sessionID: currentSessionID,
        toolUseID: pending.toolUseID,
        decision,
      });
    },
    [
      pendingConfirmation,
      currentSessionID,
      resolveConfirmation,
      setPendingConfirmation,
    ],
  );

  useEffect(() => {
    setCurrentRoutineID(routine?.id ?? null);
  }, [routine?.id, setCurrentRoutineID]);

  // [TFIX] Wire the Chat history view to feathers `agentSessions`.
  // Without this, ChatHistoryView was always rendered with sessions={[]}
  // and the list looked empty regardless of postgres state.
  const feathersApp = getApp();

  const chatHistoryOperations = useMemo<ChatHistoryOperations>(
    () => ({
      list: async ({ routineID: rid }) => {
        const findQuery: Record<string, unknown> = {
          $sort: { updatedAt: -1 },
          $limit: 100,
        };

        if (rid !== null) findQuery.routineID = rid;

        // `agentSessions` isn't on the typed Services map yet — cast
        // through `any` until the typed entry lands. The runtime
        // service name is camelCase per feathers-bot's
        // `initializeAgentSessionsService`.
        const result = await (feathersApp.service as any)('agentSessions').find(
          { query: findQuery },
        );

        const rows: any[] = Array.isArray(result) ? result : result?.data ?? [];

        return rows.map((r) => ({
          id: String(r.id),
          routineID: r.routineID ?? null,
          routineName: null,
          title:
            typeof r.title === 'string' && r.title.length > 0
              ? r.title
              : 'Untitled chat',
          lastUpdatedAt: new Date(
            r.updatedAt ?? r.createdAt ?? Date.now(),
          ).getTime(),
        }));
      },
      rename: async (sessionID, newTitle) => {
        const row = await (feathersApp.service as any)('agentSessions').patch(
          sessionID,
          { title: newTitle },
        );

        return {
          id: String(row.id),
          routineID: row.routineID ?? null,
          routineName: null,
          title: row.title,
          lastUpdatedAt: new Date(row.updatedAt ?? Date.now()).getTime(),
        };
      },
      remove: async (sessionID) => {
        await (feathersApp.service as any)('agentSessions').remove(sessionID);
      },
    }),
    [feathersApp],
  );

  const {
    sessions: historySessions,
    renameSession: renameHistorySession,
    deleteSession: deleteHistorySession,
  } = useChatHistory({
    routineID: null, // global view — all sessions
    operations: chatHistoryOperations,
  });

  // [TFIX] Seed message history from feathers `agentMessages` whenever
  // currentSessionID transitions to a non-null value (either via a
  // routine-binding hook OR via clicking a row in the Chat history
  // view). The /events stream still owns deltas for live turns; this
  // seed populates the past.
  useEffect(() => {
    if (currentSessionID === null) return undefined;

    let canceled = false;
    const sessionID = currentSessionID;

    (async () => {
      try {
        const result = await (feathersApp.service as any)('agentMessages').find(
          { query: { sessionID, $sort: { sequence: 1 } } },
        );

        const rows: unknown[] = Array.isArray(result)
          ? result
          : result?.data ?? [];

        if (canceled) return;

        const seeded: ChatMessage[] = [];
        // Index of the in-flight tool group bubble (or -1 if none).
        // A persisted assistant turn may end with one or more
        // tool_use blocks; the matching tool_result blocks live on
        // the next `tool` row and we append into the same bubble.
        let currentToolGroupIdx = -1;

        for (const row of rows) {
          if (typeof row !== 'object' || row === null) continue;

          const candidate = row as {
            id?: unknown;
            role?: unknown;
            content?: unknown;
          };

          if (
            typeof candidate.id !== 'string' ||
            !Array.isArray(candidate.content)
          ) {
            // eslint-disable-next-line no-console
            console.warn(
              'RoutineAgentChatMount: skipping malformed agentMessages row',
              row,
            );

            continue;
          }

          if (candidate.role === 'user' || candidate.role === 'assistant') {
            const { role } = candidate;
            let textForBubble = '';

            // Walk content blocks in order. Text blocks accumulate
            // into one bubble; each tool_use block starts (or
            // appends to) a per-turn tool group below it.
            for (const rawBlock of candidate.content) {
              if (typeof rawBlock !== 'object' || rawBlock === null) continue;

              const block = rawBlock as {
                type?: unknown;
                text?: unknown;
                id?: unknown;
                name?: unknown;
              };

              if (block.type === 'text' && typeof block.text === 'string') {
                textForBubble += block.text;

                continue;
              }

              if (
                block.type === 'tool_use' &&
                typeof block.id === 'string' &&
                typeof block.name === 'string'
              ) {
                // Flush any pending text into its own bubble first
                // so the tool card renders after the text.
                if (textForBubble.length > 0) {
                  seeded.push({
                    id: `${candidate.id}-text`,
                    messageID: candidate.id,
                    role,
                    text: textForBubble,
                  });

                  textForBubble = '';
                }

                const item = {
                  id: block.id,
                  label: block.name,
                  status: 'running' as const,
                };

                if (currentToolGroupIdx < 0) {
                  seeded.push({
                    id: `${candidate.id}-tools`,
                    messageID: candidate.id,
                    role: 'assistant',
                    text: '',
                    card: {
                      kind: 'toolGroup',
                      tools: [item],
                      controlled: true,
                    },
                  });

                  currentToolGroupIdx = seeded.length - 1;
                } else {
                  const target = seeded[currentToolGroupIdx];

                  if (target.card?.kind === 'toolGroup') {
                    seeded[currentToolGroupIdx] = {
                      ...target,
                      card: {
                        ...target.card,
                        tools: [...target.card.tools, item],
                      },
                    };
                  }
                }
              }
            }

            if (textForBubble.length > 0) {
              seeded.push({
                id: candidate.id,
                messageID: candidate.id,
                role,
                text: textForBubble,
              });
            }

            continue;
          }

          if (candidate.role === 'tool') {
            // Patch tool_result blocks into the matching tool item
            // of the most recent open tool group.
            if (currentToolGroupIdx < 0) continue;

            const target = seeded[currentToolGroupIdx];

            if (target.card?.kind !== 'toolGroup') continue;

            let { tools } = target.card;

            for (const rawBlock of candidate.content) {
              if (typeof rawBlock !== 'object' || rawBlock === null) continue;

              const block = rawBlock as {
                type?: unknown;
                toolUseId?: unknown;
                tool_use_id?: unknown;
                isError?: unknown;
                is_error?: unknown;
              };

              // BE persists tool_result blocks with camelCase
              // `toolUseId` (lowercase 'd'). Accept snake_case too
              // for forward-compat with any future migration.
              let useID: string | null = null;

              if (typeof block.toolUseId === 'string') {
                useID = block.toolUseId;
              } else if (typeof block.tool_use_id === 'string') {
                useID = block.tool_use_id;
              }

              if (block.type !== 'tool_result' || useID === null) {
                continue;
              }

              const errorFlag =
                block.isError === true || block.is_error === true;

              const nextStatus = errorFlag
                ? ('error' as const)
                : ('complete' as const);

              tools = tools.map((t) =>
                t.id === useID ? { ...t, status: nextStatus } : t,
              );
            }

            seeded[currentToolGroupIdx] = {
              ...target,
              card: { ...target.card, tools },
            };

            // After a tool row we expect the next assistant turn
            // to start a fresh group, mirroring live-event behavior.
            currentToolGroupIdx = -1;
          }
        }

        if (canceled) return;

        setMessages(seeded);
      } catch (err) {
        if (canceled) return;

        // eslint-disable-next-line no-console
        console.warn(
          'RoutineAgentChatMount: failed to load agentMessages history',
          err,
        );
      }
    })();

    return () => {
      canceled = true;
    };
  }, [currentSessionID, feathersApp, setMessages]);

  const handleLandingSelect = useCallback(
    (option: LandingOption) => {
      sendMessage(landingOptionLabel(option)).catch(() => {
        // sendMessage flips the user message to sendStatus: 'failed'
        // on failure so the inline retry affordance surfaces it; no
        // banner needed here.
      });
    },
    [sendMessage],
  );

  // Persist thumbs feedback as an `agentFeedback` row. The backend forwards
  // it to Braintrust (keyed on the rated message's turn). The UI sentiment
  // (`positive`/`negative`) maps to the schema's `up`/`down`. Best-effort:
  // a failed create must not disrupt the chat, so rejections are swallowed.
  const handleFeedbackSubmit = useCallback(
    (payload: FeedbackPayload) => {
      // Snapshot the durable ids of the messages currently in view so the
      // feedback row records the conversation context it was given against.
      const conversationSnapshotMessageIDs = Array.from(
        new Set(
          messages
            .map((m) => m.messageID)
            .filter((id): id is string => id !== undefined),
        ),
      );

      const input: AgentFeedbackCreateInput = {
        sessionID: payload.sessionID,
        messageID: payload.messageID,
        sentiment: payload.sentiment === 'positive' ? 'up' : 'down',
        content: payload.content ?? null,
        conversationSnapshotMessageIDs,
      };

      // `agentFeedback` isn't on the typed Services map yet — cast
      // through `any` until the typed entry lands.
      (feathersApp.service as any)('agentFeedback')
        .create(input)
        .catch((err: unknown) => {
          // Best-effort: feedback failures are non-fatal to the chat.
          // eslint-disable-next-line no-console
          console.warn(
            'RoutineAgentChatMount: failed to persist agentFeedback',
            err,
          );
        });
    },
    [feathersApp, messages],
  );

  const isHistoryView = chatView === 'history';

  // Wrap the provider's `startNewChat` so the mount also clears the
  // message pipeline. The provider can only clear what it owns
  // (currentSessionID, pendingConfirmation, chatView); reset()
  // handles the rest.
  const handleStartNewChat = useCallback(() => {
    startNewChat();
    reset();
  }, [startNewChat, reset]);

  const menuItems = useMemo<ChatHeaderMenuItem[] | undefined>(() => {
    if (isHistoryView) return undefined;

    return [
      {
        label: 'New chat',
        iconKind: 'plus',
        onClick: handleStartNewChat,
      },
      {
        label: 'Chat history',
        iconKind: 'clock',
        onClick: () => setChatView('history'),
      },
    ];
  }, [isHistoryView, handleStartNewChat, setChatView]);

  const leftAction = useMemo(() => {
    if (!isHistoryView) return undefined;

    return {
      iconKind: 'chevronLeft' as const,
      ariaLabel: 'Back to chat',
      onClick: () => setChatView('chat'),
    };
  }, [isHistoryView, setChatView]);

  const headerTitle = isHistoryView ? 'Chat history' : undefined;

  const renderFeedback = useCallback(
    (message: ChatMessage) => {
      if (currentSessionID === null) return null;
      if (message.role !== 'assistant') return null;
      if (message.isStreaming) return null;
      // Feedback targets the durable message id (not the per-bubble React
      // key); without it the backend can't resolve the rated message.
      if (message.messageID === undefined) return null;

      // History seeding fans one backend message out into several bubbles
      // (text-before-tools + tool group + trailing text) that share a
      // messageID. Render the thumbs only under the last text bubble for
      // that messageID so a single backend message can't collect multiple
      // agentFeedback rows.
      const lastTextBubbleForMessage = messages
        .filter(
          (m) =>
            m.messageID === message.messageID &&
            m.role === 'assistant' &&
            m.card === undefined,
        )
        .pop();

      if (lastTextBubbleForMessage?.id !== message.id) return null;

      return (
        <MessageFeedback
          sessionID={currentSessionID}
          messageID={message.messageID}
          onSubmit={handleFeedbackSubmit}
        />
      );
    },
    [currentSessionID, handleFeedbackSubmit, messages],
  );

  return (
    <>
      <ChatPanelOverlay
        isOpen={isOpen}
        onClose={close}
        title={headerTitle}
        menuItems={menuItems}
        leftAction={leftAction}
        body={
          isHistoryView ? (
            <ChatHistoryView
              sessions={historySessions}
              activeSessionID={currentSessionID}
              onLoad={(sessionID) => {
                setCurrentSessionID(sessionID);
                setChatView('chat');
              }}
              onRename={(sessionID, newTitle) => {
                renameHistorySession(sessionID, newTitle).catch(() => {
                  // useChatHistory handles rollback + onError; swallow
                  // here so the inline handler doesn't blow up.
                });
              }}
              onRequestDelete={(sessionID) => {
                deleteHistorySession(sessionID).catch(() => {
                  // same — rollback handled by the hook.
                });
              }}
            />
          ) : (
            <>
              {streamError ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="tw-px-16 tw-py-8 tw-text-13 tw-text-red"
                >
                  Stream disconnected: {streamError.message}
                </div>
              ) : null}
              {/* Terminal agent error (token budget exhausted, LLM
                  unavailable, …). role="alert" because it's the end of the
                  turn and the operator must act (e.g. start a new chat),
                  unlike the transient disconnect status above. */}
              {agentError ? (
                <div
                  role="alert"
                  data-testid="chat-error-banner"
                  className="tw-px-16 tw-py-8 tw-text-13 tw-font-medium tw-text-red"
                >
                  {agentError.message}
                </div>
              ) : null}
              {messages.length === 0 ? (
                <LandingHero onSelectOption={handleLandingSelect} />
              ) : (
                <MessageList
                  messages={messages}
                  renderFeedback={renderFeedback}
                  onRetry={retryMessage}
                  onCardNavigate={navigate}
                  isThinking={isThinking}
                />
              )}
            </>
          )
        }
        footer={
          isHistoryView ? null : (
            <ChatInput onSubmit={sendMessage} disabled={isSending} />
          )
        }
      />
      <ConfirmationModal
        pending={pendingConfirmation}
        onResolve={handleConfirmationResolve}
        isResolving={isResolvingConfirmation}
      />
    </>
  );
}

interface RoutineAgentChatMountProps {
  children: React.ReactNode;
  /**
   * Backend adapter. Defaults to {@link createRealChatTransport},
   * which hits `/api/v1/agent/*` directly. Tests and the UI-only
   * dev harness pass an in-process mock that satisfies the same
   * {@link ChatTransport} contract.
   */
  transport?: ChatTransport;
  /**
   * Initial chat-panel state. Used by tests / dev harness to seed
   * `currentSessionID` without going through the routine session
   * binding hook. Production mounts leave this unset and let the
   * binding hook populate the provider.
   */
  initialState?: React.ComponentProps<typeof ChatPanelProvider>['initialState'];
}

/**
 * Routine Agent Chat mount point. Wraps its children (the layout
 * navbar + page content) in `ChatPanelProvider` so the navbar's
 * launcher button can open the panel via `useChatPanelOptional`,
 * then renders the overlay alongside them. Caller gates on both
 * `appRedesign` and `routineAgentChat` per the `app-redesign`
 * skill — see `PrivatePageLayoutInner.tsx`.
 */
export function RoutineAgentChatMount({
  children,
  transport,
  initialState,
}: RoutineAgentChatMountProps) {
  // Memoize the default transport so a re-render of the layout
  // doesn't tear down the events subscription on the body.
  const resolvedTransport = useMemo(
    () => transport ?? createRealChatTransport(),
    [transport],
  );

  return (
    <ChatPanelProvider initialState={initialState}>
      {children}
      <RoutineAgentChatBody transport={resolvedTransport} />
    </ChatPanelProvider>
  );
}
